package com.snakesworld.game.domain

import com.snakesworld.game.engine.Direction
import com.snakesworld.game.engine.GameConstants
import com.snakesworld.game.engine.SnakeSegment
import com.snakesworld.game.engine.Vector2D
import kotlin.math.PI

/**
 * Kinematic chain physics and orthogonal heading resolution.
 * Enforces strict 4-directional movement, 180° reversal lockout, and constant base speed.
 */
object SnakeKinematics {

    /**
     * Validates if a directional change is permitted.
     * Prevents immediate 180° reversals into the trailing body.
     */
    fun isValidTurn(current: Direction, requested: Direction): Boolean {
        return !current.isOpposite(requested)
    }

    /**
     * Advance snake head by deltaTime.
     * Base speed is strictly constant and invariant to mass or pellet count.
     */
    fun moveHead(
        currentHead: Vector2D,
        direction: Direction,
        speed: Float,
        deltaTime: Float,
    ): Vector2D {
        val displacement = direction.toVector() * (speed * deltaTime)
        return currentHead + displacement
    }

    /**
     * Creates straight-line trailing body segments behind the head.
     */
    fun createInitialSegments(
        headPosition: Vector2D,
        direction: Direction,
        count: Int,
        spacing: Float = GameConstants.SEGMENT_SPACING,
    ): List<SnakeSegment> {
        val backward = direction.toVector() * -1f
        return (1..count).map { i ->
            SnakeSegment(
                position = headPosition + (backward * (i * spacing)),
                angle = direction.angle
            )
        }
    }

    /**
     * Advances the snake by one move step:
     * - Head advances in [direction] by [stepSize].
     * - A new body segment is added at the previous head location.
     * - Tail segment(s) are removed to match [targetSegmentCount].
     * This ensures body segments stay at fixed coordinates along the exact historical
     * corner points without tail swinging or rotational artifacts.
     */
    fun advanceSnake(
        currentHead: Vector2D,
        direction: Direction,
        segments: List<SnakeSegment>,
        targetSegmentCount: Int,
        stepSize: Float = GameConstants.SEGMENT_SPACING,
    ): Pair<Vector2D, List<SnakeSegment>> {
        val newHead = currentHead + (direction.toVector() * stepSize)
        val newSegments = ArrayList<SnakeSegment>(segments.size + 1)
        newSegments.add(SnakeSegment(currentHead, direction.angle))
        newSegments.addAll(segments)
        while (newSegments.size > targetSegmentCount) {
            newSegments.removeAt(newSegments.size - 1)
        }
        return Pair(newHead, newSegments)
    }

    /**
     * Reconstructs body segments along historical path points using inverse kinematics
     * with fixed equidistant spacing [spacing].
     */
    fun solveSegmentChain(
        headPosition: Vector2D,
        pathHistory: List<Vector2D>,
        targetSegmentCount: Int,
        spacing: Float = GameConstants.SEGMENT_SPACING,
    ): List<SnakeSegment> {
        if (targetSegmentCount <= 0) return emptyList()

        val fullPath = ArrayList<Vector2D>(pathHistory.size + 1).apply {
            add(headPosition)
            addAll(pathHistory)
        }

        val segments = ArrayList<SnakeSegment>(targetSegmentCount)
        var pathIndex = 0
        var currentPos = headPosition
        var accumulatedDistance = 0f

        for (segIdx in 0 until targetSegmentCount) {
            val targetDistance = (segIdx + 1) * spacing

            while (pathIndex < fullPath.size - 1 && accumulatedDistance < targetDistance) {
                val p1 = fullPath[pathIndex]
                val p2 = fullPath[pathIndex + 1]
                val segmentLen = p1.distanceTo(p2)

                if (accumulatedDistance + segmentLen >= targetDistance) {
                    val remaining = targetDistance - accumulatedDistance
                    val ratio = if (segmentLen > 0.0001f) remaining / segmentLen else 0f
                    val segPos = p1.lerp(p2, ratio)
                    val angle = (p1 - p2).angle()
                    segments.add(SnakeSegment(segPos, angle))
                    currentPos = segPos
                    break
                } else {
                    accumulatedDistance += segmentLen
                    pathIndex++
                }
            }

            // If path history is shorter than required length, trail behind last segment
            if (segments.size <= segIdx) {
                val lastPos = if (segments.isNotEmpty()) segments.last().position else currentPos
                val dirVector = if (pathIndex < fullPath.size) (fullPath.last() - currentPos).normalized() else Vector2D(-1f, 0f)
                val fallbackPos = lastPos + (dirVector * spacing)
                segments.add(SnakeSegment(fallbackPos, dirVector.angle()))
            }
        }

        return segments
    }

    /**
     * Normalizes angle difference to [-PI, PI] to prevent 360-degree abrupt spinning.
     */
    fun wrapAngleDelta(targetAngle: Float, currentAngle: Float): Float {
        val diff = (targetAngle - currentAngle + PI.toFloat()) % (2.0f * PI.toFloat()) - PI.toFloat()
        return if (diff < -PI.toFloat()) diff + (2.0f * PI.toFloat()) else diff
    }
}
